import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials } from '../features/auth/authSlice';
import { BASE } from '../config/setting';

export default function TwoFactorVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [token, setToken] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const authToken = useSelector(state => state.auth.token);

  const { email, password } = location.state || {};

  // Navigate when token is set
  useEffect(() => {
    if (authToken) {
      navigate('/', { replace: true });
    }
  }, [authToken, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(null);
    setVerifying(true);

    try {
      if (!email || !password) {
        throw new Error('Session expired. Please login again.');
      }

      const response = await fetch(`${BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password,
          twoFactorCode: token
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Invalid 2FA token');
      }

      const data = await response.json();
      
      // Backend now returns token directly when 2FA code is provided
      if (data.token) {
        // Dispatch credentials - useEffect will handle navigation
        dispatch(setCredentials({
          user: data.user,
          token: data.token
        }));
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleBack = () => {
    navigate('/login');
  };

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          {/* Header Card */}
          <div className="card border-0 shadow-lg mb-4">
            <div className="card-body text-center py-5">
              <div style={{ fontSize: '3.5rem' }}>🔐</div>
              <h2 className="mt-3 mb-2">Two-Factor Authentication</h2>
              <p className="text-muted">Enter the 6-digit code from your authenticator app</p>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger">
              ⚠️ {error}
            </div>
          )}

          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <form onSubmit={handleVerify}>
                <div className="mb-4">
                  <label className="form-label text-center w-100">
                    <strong>📱 Authentication Code</strong>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg text-center"
                    placeholder="000000"
                    value={token}
                    onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    pattern="\d{6}"
                    required
                    autoFocus
                    disabled={verifying}
                    style={{ letterSpacing: '0.5em', fontSize: '2rem' }}
                  />
                  <div className="form-text text-center mt-2">
                    Open your Google Authenticator app to get the code
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-lg w-100 mb-3"
                  disabled={verifying || token.length !== 6}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {verifying ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Verifying...
                    </>
                  ) : (
                    <>Verify & Login →</>
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-outline-secondary w-100"
                  onClick={handleBack}
                  disabled={verifying}
                >
                  ← Back to Login
                </button>
              </form>
            </div>
          </div>

          <div className="text-center mt-4">
            <small className="text-muted">
              💡 Lost access to your authenticator? Contact your administrator
            </small>
          </div>
        </div>
      </div>
    </div>
  );
}
